import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";

import App from "./App";
import "./styles/global.css";
import "./styles/docking-theme.css";

// Vite sets BASE_URL from the `base` config (e.g. "/thread/" in production, "/" in dev).
// Strip trailing slash for BrowserRouter basename (it expects "/thread" not "/thread/").
const basename = import.meta.env.BASE_URL.replace(/\/$/, "") || "/";

// The Thread CDN can serve the SPA shell at the host root. React Router's
// basename expects the URL to already live under /thread, so move root visitors
// there before mounting the app.
const shouldRedirectHostRoot = basename !== "/" && window.location.pathname === "/";
if (shouldRedirectHostRoot) {
  window.location.replace(`${basename}/${window.location.search}${window.location.hash}`);
}

async function bootstrap() {
  // AG Grid module registration is required since v35. Thread V3 uses paid
  // row grouping/aggregation features, so register Enterprise whenever the
  // package is installed; apply the real license key when the environment has it.
  const agLicenseKey = import.meta.env.VITE_AG_GRID_LICENSE_KEY?.trim();
  ModuleRegistry.registerModules([AllCommunityModule]);

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <BrowserRouter basename={basename}>
        <App />
      </BrowserRouter>
    </StrictMode>,
  );

  // Do not make the whole application wait for the optional Enterprise bundle.
  // On a cold development start this import can take long enough to leave every
  // route — including standalone demos — on a blank screen. Community modules
  // are sufficient to mount the shell; Enterprise augments the registry when it
  // becomes available.
  void import("ag-grid-enterprise")
    .then(({ AllEnterpriseModule, LicenseManager }) => {
      if (agLicenseKey) LicenseManager.setLicenseKey(agLicenseKey);
      ModuleRegistry.registerModules([AllEnterpriseModule]);
    })
    .catch(() => undefined);
}

if (!shouldRedirectHostRoot) {
  void bootstrap();
}
